import { parseISO, format, addMinutes, parse } from 'date-fns';
import { DssAppointmentOption } from 'types/signingAppointment/CreateSigningAppointmentRequest';

interface DssParams {
  isDssDateRange?: boolean;
  dssDate?: string;
  dssDateRangeStart?: string;
  dssDateRangeEnd?: string;
  dssStartTime?: string;
  dssEndTime?: string;
}

const formatTimeToAmPm = (time?: string): string => {
  if (!time) return '';
  const parsedTime = parseISO(`1970-01-01T${time}`);
  return format(parsedTime, 'h:mm a');
};

const formatDate = (date?: string): string => {
  if (!date) return '';
  const parsedDate = parseISO(date);
  return format(parsedDate, 'MM/dd/yyyy');
};

export const getDssDateTimeInfo = (paramsOrAppointments: DssParams | DssAppointmentOption[]): string => {
  if (Array.isArray(paramsOrAppointments)) {
    const validAppointments = paramsOrAppointments.filter(
      (a): a is Required<DssAppointmentOption> => !!a.appointmentDate && !!a.appointmentTime
    );

    if (validAppointments.length === 0) return '';

    validAppointments.sort((a, b) => {
      const dateComparison = a.appointmentDate!.localeCompare(b.appointmentDate!);
      return dateComparison !== 0 ? dateComparison : a.appointmentTime!.localeCompare(b.appointmentTime!);
    });

    const initialDate = formatDate(validAppointments[0].appointmentDate);
    const finalDate = formatDate(validAppointments[validAppointments.length - 1].appointmentDate);

    const initialTime = formatTimeToAmPm(validAppointments[0].appointmentTime);
    let finalTime = formatTimeToAmPm(validAppointments[validAppointments.length - 1].appointmentTime);

    // Add 1 hour to the final time for display if we’re using an array input
    const lastAppointmentEnd = addMinutes(
      parse(validAppointments[validAppointments.length - 1].appointmentTime!, 'HH:mm:ss', new Date()),
      60
    );
    finalTime = format(lastAppointmentEnd, 'h:mm a');

    return initialDate === finalDate
      ? `${initialDate}, ${initialTime} - ${finalTime}`
      : `${initialDate} - ${finalDate}, ${initialTime} - ${finalTime}`;
  } else {
    // Handle DssParams
    const { isDssDateRange, dssDate, dssDateRangeStart, dssDateRangeEnd, dssStartTime, dssEndTime } =
      paramsOrAppointments;

    const initialTime = formatTimeToAmPm(dssStartTime);
    const finalTime = formatTimeToAmPm(dssEndTime);

    if (isDssDateRange) {
      const initialDate = formatDate(dssDateRangeStart);
      const finalDate = formatDate(dssDateRangeEnd);
      return `${initialDate} - ${finalDate}, ${initialTime} - ${finalTime}`;
    }

    const formattedDate = formatDate(dssDate);
    return `${formattedDate}, ${initialTime} - ${finalTime}`;
  }
};
